import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { WORK_AGREEMENT_ROLES } from "@xinghai/shared";
import { Button, EmptyState, Field, Icon, Input, Tag, useDesignSystemTheme } from "@xinghai/ui";
import { ApiError, authApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { COPYRIGHT_LINE } from "../../lib/constants";
import {
  WORK_AGREEMENT_AGREE_LABEL,
  WORK_AGREEMENT_CLAUSES,
  WORK_AGREEMENT_SUMMARY,
  WORK_AGREEMENT_TITLE,
  WORK_AGREEMENT_VERSION,
} from "./workAgreementText";
import { qk } from "../../lib/queries";
import { ROLE_LABELS, homeFor, landingRole, primaryRole } from "../../app/roles";
import { useTheme } from "../../app/theme";
import { useAuth } from "./useAuth";
import { confirmIssue, passwordHint, passwordIssues } from "./passwordRules";

const TRACK_LABELS: Record<string, string> = {
  E1_phonetics: "语音学方向",
  E2_vocal: "声乐方向",
  E3_extra: "第三方向",
};

const INVITE_MESSAGES: Record<string, string> = {
  INVITE_EXPIRED: "这个邀请链接已经过期了（有效期 72 小时）。请让发给你的人重新生成一个。",
  INVITE_USED: "这个邀请链接的次数已经用完了。请让发给你的人再生成一条。",
  INVITE_REVOKED: "这个邀请链接已经被撤销了。请让发给你的人重新生成一个。",
  INVITE_INVALID: "这个邀请链接无效。请确认链接是完整复制过来的。",
  INVITE_NOT_FOUND: "这个邀请链接无效。请确认链接是完整复制过来的。",
  USERNAME_TAKEN: "这个用户名已经有人用了，换一个。",
};

function inviteProblem(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  if (INVITE_MESSAGES[err.code]) return INVITE_MESSAGES[err.code]!;
  if (err.status === 404) return "这个邀请链接无效或已失效。";
  return friendlyMessage(err, "没能完成注册，请重试。");
}

/** ACCOUNTS.md §2 — 专家 / 仲裁 / 课程团队 / 教师 通过邀请链接开号. */
export function InviteAcceptPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  useDesignSystemTheme(theme);

  const preview = useQuery({
    queryKey: qk.invitePreview(token),
    queryFn: () => authApi.invitePreview(token),
    retry: false,
  });

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [workAgreed, setWorkAgreed] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);

  const accept = useMutation({
    mutationFn: () =>
      authApi.acceptInvite(token, {
        username: username.trim(),
        password,
        display_name: displayName.trim(),
        work_agreement: { version: WORK_AGREEMENT_VERSION, accepted: true },
      }),
    onSuccess: (next) => {
      qc.setQueryData(qk.me, next);
      void navigate(homeFor(primaryRole(next.user.roles)), { replace: true });
    },
    onError: (err) => setError(inviteProblem(err) ?? "没能完成注册，请重试。"),
  });

  const invite = preview.data?.invitation;
  const needsAgreement = Boolean(
    invite?.roles.some((r) => (WORK_AGREEMENT_ROLES as readonly string[]).includes(r)) &&
      me?.user.work_agreement_version !== WORK_AGREEMENT_VERSION,
  );
  const join = useMutation({
    mutationFn: () =>
      authApi.joinInvite(
        token,
        needsAgreement && workAgreed
          ? { work_agreement: { version: WORK_AGREEMENT_VERSION, accepted: true } }
          : {},
      ),
    onSuccess: (next) => {
      qc.setQueryData(qk.me, next);
      void navigate(homeFor(landingRole(next.user.roles)), { replace: true });
    },
    onError: (err) => setError(inviteProblem(err) ?? "没能加入，请重试。"),
  });

  if (preview.isPending || authLoading) return <p className="xz-container gz-muted">正在检查邀请链接…</p>;

  if (preview.isError) {
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-warning" size={24} />}
          title="这个邀请链接用不了"
          description={inviteProblem(preview.error) ?? "请联系发给你链接的人。"}
          action={
            <Link className="gz-button" to="/login">
              去登录
            </Link>
          }
        />
      </div>
    );
  }

  // `GET auth/invite/:code` wraps the invitation: { invitation: {...} }.
  if (!invite) {
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-warning" size={24} />}
          title="这个邀请链接用不了"
          description="没有读到邀请信息，请联系发给你链接的人重新生成一个。"
          action={
            <Link className="gz-button" to="/login">
              去登录
            </Link>
          }
        />
      </div>
    );
  }
  const roleText = invite.roles.map((r) => ROLE_LABELS[r] ?? r).join("、");
  const classText = invite.class_name ?? invite.class_id ?? "";
  const joinLabel = classText ? `加入 ${classText} 作为 ${roleText}` : `加入，作为 ${roleText}`;

  if (me) {
    return (
      <main className="xz-login">
        <form
          className="xz-login__card gz-panel gz-panel--raised"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (needsAgreement && !workAgreed) {
              setError(`请先阅读并同意《${WORK_AGREEMENT_TITLE}》。`);
              return;
            }
            join.mutate();
          }}
        >
          <h1 className="gz-type-heading">{joinLabel}</h1>
          <p className="gz-muted">
            你已经登录为 {me.user.display_name || me.user.username}。点下面的按钮，把这个身份加到当前账号，不会新建账号。
          </p>
          <div className="xz-invite__meta">
            {invite.roles.map((r) => (
              <Tag key={r} tone="info">
                <Icon id="ui-user" size={16} /> {ROLE_LABELS[r] ?? r}
              </Tag>
            ))}
            {classText && (
              <Tag tone="neutral">
                <Icon id="ui-school" size={16} /> {classText}
              </Tag>
            )}
          </div>
          {needsAgreement && (
            <label className="xz-consent__row xz-consent__agree">
              <input
                type="checkbox"
                checked={workAgreed}
                aria-label={WORK_AGREEMENT_AGREE_LABEL}
                onChange={(e) => setWorkAgreed(e.target.checked)}
              />
              <span>{WORK_AGREEMENT_AGREE_LABEL}</span>
            </label>
          )}
          {error && (
            <p className="gz-notice gz-notice--error" role="alert">
              <Icon id="ui-error" size={20} />
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" size="lg" block loading={join.isPending}>
            {joinLabel}
          </Button>
        </form>
      </main>
    );
  }

  const pwIssues = password ? passwordIssues(password, username) : [];
  const mismatch = confirm ? confirmIssue(password, confirm) : null;
  const hint = passwordHint(password, username);

  const submit = () => {
    setError(null);
    if (username.trim().length < 3) return setError("用户名至少 3 位。");
    if (displayName.trim().length === 0) return setError("请填写你的姓名。");
    if (password.length === 0 || pwIssues.length > 0 || mismatch) {
      return setError("请先按提示修好密码。");
    }
    if (!workAgreed) return setError(`请先阅读并同意《${WORK_AGREEMENT_TITLE}》。`);
    accept.mutate();
  };

  return (
    <main className="xz-login">
      <form
        className="xz-login__card gz-panel gz-panel--raised"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <h1 className="gz-type-heading">开通你的账号</h1>
        <p className="gz-muted">这个邀请链接给你开通以下身份，用户名和密码由你自己设定。</p>

        <div className="xz-invite__meta">
          {invite.roles.map((r) => (
            <Tag key={r} tone="info">
              <Icon id="ui-user" size={16} /> {ROLE_LABELS[r] ?? r}
            </Tag>
          ))}
          {invite.annotator_track && (
            <Tag tone="neutral">
              {TRACK_LABELS[invite.annotator_track] ?? invite.annotator_track}
            </Tag>
          )}
          {(invite.class_name ?? invite.class_id) && (
            <Tag tone="neutral">
              <Icon id="ui-school" size={16} /> {invite.class_name ?? invite.class_id}
            </Tag>
          )}
        </div>
        {invite.expires_at && <p className="gz-caption">链接有效期至 {invite.expires_at}</p>}

        <Field label="用户名" hint="登录时用，3 位以上">
          <Input
            value={username}
            autoCapitalize="off"
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
          />
        </Field>
        <Field label="姓名">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field
          label="密码"
          hint={password ? `${hint.label}：${hint.advice}` : "至少 10 位，不要包含用户名"}
          error={pwIssues[0]}
        >
          <Input
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="再输一次密码" error={mismatch ?? undefined}>
          <Input
            type="password"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        <div className="xz-workagreement">
          <label className="xz-consent__row xz-consent__agree">
            <input
              type="checkbox"
              checked={workAgreed}
              aria-label={WORK_AGREEMENT_AGREE_LABEL}
              onChange={(e) => setWorkAgreed(e.target.checked)}
            />
            <span>{WORK_AGREEMENT_AGREE_LABEL}</span>
          </label>
          <p className="gz-caption">{WORK_AGREEMENT_SUMMARY}</p>
          <button
            type="button"
            className="gz-button gz-button--quiet xz-consent__more"
            aria-expanded={workOpen}
            onClick={() => setWorkOpen((v) => !v)}
          >
            <Icon id={workOpen ? "ui-collapse" : "ui-expand"} size={16} />
            {workOpen ? "收起协议全文" : "查看协议全文"}
          </button>
          {workOpen && (
            <div className="xz-consent__full gz-type-reading">
              {WORK_AGREEMENT_CLAUSES.map((c) => (
                <section key={c.number}>
                  <h2 className="gz-type-ui">
                    {c.number}、{c.title}
                  </h2>
                  {c.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>

        {error && (
          <p className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" size="lg" block loading={accept.isPending}>
          开通账号
        </Button>
        <p className="gz-caption">
          <Link to="/login" state={{ from: `/invite/${token}` }}>
            已有账号？登录后加入
          </Link>
        </p>
        <p className="gz-caption xz-login__org">{COPYRIGHT_LINE}</p>
      </form>
    </main>
  );
}
